import { MailUnsubscribeRequestSchema } from "@canvaschool/contracts";
import { useRef, useState } from "react";

import { useAuth } from "../auth/AuthProvider";
import { ErrorAlert, errorCodeOf } from "../components/ErrorAlert";
import { Link } from "../components/Link";
import { Logo } from "../components/Logo";

import type { ApiErrorCode } from "../api/client";

const INVALID_LINK =
  "링크가 올바르지 않습니다. 메일에 있는 링크를 그대로 눌러 주세요.";

/** The signed token from `?u=…&s=…`, or null when the link is broken. */
const tokenFrom = (search: string) => {
  const params = new URLSearchParams(search);
  const parsed = MailUnsubscribeRequestSchema.safeParse({
    u: params.get("u") ?? undefined,
    s: params.get("s") ?? undefined,
  });
  return parsed.success ? parsed.data : null;
};

/**
 * Unsubscribing from notice and lesson-tips mail, from the link in such a
 * mail. Nothing happens until the button is pressed: mail security scanners
 * open links in advance. Open to everyone, signed in or not.
 */
export const UnsubscribePage = ({ search }: { search: string }) => {
  const { api } = useAuth();
  const [token] = useState(() => tokenFrom(search));
  const [state, setState] = useState<"ready" | "done" | "invalid">(
    token ? "ready" : "invalid",
  );
  const [error, setError] = useState<ApiErrorCode | null>(null);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);

  const unsubscribe = async () => {
    if (!token || busyRef.current) {
      return;
    }
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      await api.unsubscribeMail(token);
      setState("done");
    } catch (failure) {
      const code = errorCodeOf(failure);
      if (code === "VALIDATION_FAILED") {
        setState("invalid");
      } else {
        setError(code);
      }
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  return (
    <div className="canvaschool-beta">
      <header className="canvaschool-beta-header">
        <Link to="/" className="canvaschool-brand">
          <Logo size={28} />
          크루바스
          <span className="canvaschool-brand-latin">Crewvas</span>
        </Link>
      </header>
      <section className="canvaschool-auth">
        <h1>크루바스 안내 메일 수신 해지</h1>
        <div aria-live="polite">
          {state === "done" && (
            <p className="canvaschool-success" role="status">
              수신을 해지했습니다. 계정·비밀번호처럼 꼭 필요한 안내는 계속 보내
              드릴 수 있습니다. 다시 받고 싶으시면 help@crewvas.com으로 알려
              주세요.
            </p>
          )}
          {state === "invalid" && (
            <p className="canvaschool-error" role="alert">
              {INVALID_LINK}
            </p>
          )}
        </div>
        {state === "ready" && (
          <>
            <p className="canvaschool-hint">
              크루바스(이전 이름 크루버스)가 보내는 수업 안내·사용법 메일을 더
              받지 않으시겠어요?
            </p>
            {error && <ErrorAlert code={error} boxed />}
            <button
              type="button"
              className="canvaschool-btn-primary"
              onClick={() => void unsubscribe()}
              disabled={busy}
              aria-busy={busy}
            >
              수신 해지
            </button>
          </>
        )}
      </section>
    </div>
  );
};
